import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core'
import { Location } from '@angular/common'
import { EntityConfiguration } from '../../../models/entity-configuration'
import { StoredEntityStateService } from '../../../../entity-store-access/services/stored-entity-state.service'
import { ActionService } from '../../../../action-module/services/action.service'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { EMPTY, Subject, Subscription } from 'rxjs'
import { EntityDialogComponent } from '../../entity-dialog/entity-dialog.component'
import { TableUtils } from '../../dynamic-table/table-utils'
import { catchError, map, switchMap, take, tap } from 'rxjs/operators'
import { EntityConfigService, Field } from '../../..'
import { StoredEntityService } from '../../../../entity-store-access'
import { NotificationService } from '../../../../../services/notification.service'
import { ConfirmationService } from 'primeng/api'

@Component({
  selector: 'jml-lib-entity-actions',
  templateUrl: './entity-actions.component.html',
  styleUrls: ['./entity-actions.component.scss']
})
export class EntityActionsComponent implements OnChanges, OnDestroy {
  @Input() configuration: EntityConfiguration
  @Input() entityId
  @Input() orientation: string

  entity: any

  private editableFields: Field[]
  private dialogRef: DynamicDialogRef
  private subscriptions: Subscription[] = []

  constructor(
    private actionService: ActionService,
    private storedEntityWrapper: StoredEntityStateService,
    private storedEntityService: StoredEntityService,
    private notificationService: NotificationService,
    private entityConfigService: EntityConfigService,
    private confirmationService: ConfirmationService,
    private location: Location,
    private dialogService: DialogService
  ) {
    storedEntityWrapper.entityUpdated$.subscribe(() =>
      console.log('entity update triggered in entity actions component')
    )
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entityId && changes.entityId?.currentValue) {
      this.subscriptions.push(
        this.storedEntityService
          .getEntity$(this.configuration.type, changes.entityId.currentValue)
          .pipe(
            switchMap((entity) => {
              return this.entityConfigService
                .getEditableFields$(this.configuration.type, { checkForReadPermission: true })
                .pipe(
                  tap((fields) => {
                    if (entity.fields['_actions_']?.some((action) => action === 'UPDATE')) {
                      this.editableFields = fields
                    }
                  }),
                  map(() => entity)
                )
            })
          )
          .subscribe((entity) => {
            this.entity = entity.fields
          })
      )
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe())
    this.dialogRef?.close()
  }

  performAction(actionHrid: string, actionButton) {
    actionButton.inProgress = true
    this.actionService.performAction(
      actionHrid,
      { dtoType: this.configuration.type, id: this.entity['id'] },
      undefined,
      (success) => {
        if (success) {
          this.storedEntityWrapper.forceRefresh()
        }
        actionButton.inProgress = false
      }
    )
  }

  updateEntity() {
    const dialogContext: { buttonTxt: string; headerTxt: string; desc: string } = TableUtils.getEntityDialogContext(
      this.configuration,
      'update'
    )
    const $onChange = new Subject()
    const $enableForm = new Subject<void>()
    this.dialogRef = this.dialogService.open(EntityDialogComponent, {
      data: {
        description: dialogContext.desc,
        entity: this.entity,
        fields: this.editableFields.filter((f) => f.showOnUpdate),
        configType: this.configuration.type,
        //mainId: this.mainId,
        buttonText: dialogContext.buttonTxt,
        desc: dialogContext.desc,
        onChange: $onChange,
        enableForm: $enableForm.asObservable()
      },
      header: dialogContext.headerTxt,
      draggable: true,
      width: '500px'
    })

    this.subscriptions.push(
      $onChange
        .pipe(
          switchMap((fields: Field[]) =>
            fields
              ? this.storedEntityService.updateEntity$(
                  this.configuration.fields,
                  this.configuration.type,
                  this.entity['id'],
                  fields,
                  this.entity['_lastUpdate_']
                )
              : EMPTY
          ),
          catchError((error, caught) => {
            console.error('[Dynamic-Table] Method updateEntity failed!', error)
            $enableForm.next()
            return caught
          }),
          take(1)
        )
        .subscribe((entity) => {
          this.entity = entity.fields
          this.notificationService.addSuccessNotification(
            `${this.configuration.singleDisplayName} '${entity.fields['_repr_']}' wurde aktualisiert.`
          )
          this.dialogRef.close()
        })
    )
  }

  deleteEntity() {
    this.confirmationService.confirm({
      message: 'Sind Sie sicher, dass Sie diesen Eintrag löschen wollen?',
      header: 'Eintrag löschen',
      key: 'global',
      accept: () => {
        this.subscriptions.push(
          this.storedEntityService.deleteEntity$(this.configuration.type, this.entity['id']).subscribe(() => {
            this.notificationService.addSuccessNotification(
              `${this.configuration.singleDisplayName} '${this.entity['_repr_']}' wurde gelöscht.`
            )
            this.location.back()
          })
        )
      }
    })
  }
}
